import React, { useEffect, useMemo, useState } from "react";
import { Button, Alert, Spinner, Card, ButtonGroup } from "react-bootstrap";
import { BillingClient, FileResponse, PortalRequest, StartCheckoutDto } from "../web-api-client.ts";

type BillingStatus =
  | "pending_setup"
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "cancel_scheduled";

type BillingInterval = "month" | "year";

type BillingStatusResponse = {
  status: BillingStatus;
  amountCents: number;
  currency: string;
  currentPeriodEnd?: string | null;
  trialEnd?: string | null;
  billingInterval?: BillingInterval | string | null; // server may not return yet; handle defensively
  cancelAtPeriodEnd?: boolean | null;
  cancelAt?: string | null;
  canceledAt?: string | null;
};

async function fileResponseToJson<T>(fr: FileResponse): Promise<T> {
  const text = await fr.data.text();
  return JSON.parse(text) as T;
}

function formatMoney(cents: number, currency: string) {
  const amount = cents / 100;
  return amount.toLocaleString(undefined, { style: "currency", currency: currency.toUpperCase() });
}

function normalizeInterval(v: any): BillingInterval {
  return String(v || "month").toLowerCase() === "year" ? "year" : "month";
}

function computeAnnualCentsFromMonthly(monthlyCents: number) {
  return Math.round(monthlyCents * 12 * 0.9);
}

const BillingComplete: React.FC = () => {
  const [data, setData] = useState<BillingStatusResponse | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const billingClient = useMemo(() => new BillingClient(), []);

  async function fetchBillingStatus(): Promise<BillingStatusResponse> {
    const res = await fetch("/api/Billing/status", { credentials: "include" });
    if (!res.ok) throw new Error("Unable to fetch billing status.");
    return res.json();
  }

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchBillingStatus();
        setData(d);
      } catch (e: any) {
        setError(e.message ?? "Failed to load billing status.");
      }
    })();
  }, []);

  const selectedInterval: BillingInterval = normalizeInterval(data?.billingInterval);
  const [interval, setInterval] = useState<BillingInterval>("month");

  // When billing data loads, default selector to whatever the server says (or month)
  useEffect(() => {
    if (data) setInterval(selectedInterval);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  async function startCheckout() {
    setError(null);
    setWorking(true);
    try {
      const dto: StartCheckoutDto = {
        dealerId: undefined as any,
        billingInterval: interval
      };

      const fr = await billingClient.billing_StartCheckout(dto);
      const { sessionUrl } = await fileResponseToJson<{ sessionUrl: string }>(fr);
      window.location.href = sessionUrl;
    } catch (e: any) {
      setWorking(false);
      setError(e?.message ?? "Failed to start checkout.");
    }
  }

  async function openPortal() {
    setError(null);
    setWorking(true);
    try {
      const dto: PortalRequest = { dealerId: undefined as any };
      const fr = await billingClient.billing_CreatePortal(dto);
      const { sessionUrl } = await fileResponseToJson<{ sessionUrl: string }>(fr);
      window.location.href = sessionUrl;
    } catch (e: any) {
      setWorking(false);
      setError(e?.message ?? "Failed to open portal.");
    }
  }

  const now = Date.now();
  const currentPeriodEndMs = data?.currentPeriodEnd ? new Date(data.currentPeriodEnd).getTime() : 0;

  const blocked =
    !data
      ? false
      : data.status === "pending_setup" ||
        data.status === "past_due" ||
        data.status === "canceled" ||
        (data.status === "cancel_scheduled" && now >= currentPeriodEndMs);

  const showCancelScheduledBanner = data?.status === "cancel_scheduled" && now < currentPeriodEndMs;

  const monthlyCents = data?.amountCents ?? 0;
  const currency = data?.currency ?? "usd";
  const annualCents = computeAnnualCentsFromMonthly(monthlyCents);

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
      <Card className="p-4" style={{ maxWidth: 560, width: "100%" }}>
        <h3 className="mb-3">Complete Billing</h3>
        <p className="text-muted mb-4">To access QCAR, please complete your subscription.</p>

        {error && <Alert variant="danger">{error}</Alert>}

        {!data ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" role="status" />
            <div>Loading…</div>
          </div>
        ) : showCancelScheduledBanner ? (
          <>
            <Alert variant="info" className="mb-4">
              Your subscription is set to end on{" "}
              <strong>{new Date(data.currentPeriodEnd!).toLocaleString()}</strong>. You have access until then.
              Manage billing in the portal.
            </Alert>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" disabled={working} onClick={openPortal}>
                Manage in Portal
              </Button>
            </div>
          </>
        ) : blocked ? (
          <>
            {/* Interval selector (only relevant when paying / resuming) */}
            {(data.status === "pending_setup" || data.status === "canceled") && (
              <div className="mb-3">
                <div className="fw-semibold mb-2">Choose billing interval</div>
                <ButtonGroup className="w-100">
                  <Button
                    variant={interval === "month" ? "primary" : "outline-primary"}
                    onClick={() => setInterval("month")}
                    disabled={working}
                  >
                    Monthly ({formatMoney(monthlyCents, currency)}/mo)
                  </Button>
                  <Button
                    variant={interval === "year" ? "primary" : "outline-primary"}
                    onClick={() => setInterval("year")}
                    disabled={working}
                  >
                    Annual ({formatMoney(annualCents, currency)}/yr, save 10%)
                  </Button>
                </ButtonGroup>
              </div>
            )}

            <div className="d-flex flex-wrap gap-2">
              {(data.status === "pending_setup" || data.status === "canceled") && (
                <Button disabled={working} onClick={startCheckout}>
                  {working ? "Redirecting…" : "Complete Payment"}
                </Button>
              )}

              {data.status === "past_due" && (
                <Button disabled={working} onClick={openPortal}>
                  {working ? "Opening…" : "Update Payment Method"}
                </Button>
              )}

              <Button variant="outline-secondary" disabled={working} onClick={openPortal}>
                {working ? "Opening…" : "View Invoices / Manage"}
              </Button>
            </div>
          </>
        ) : (
          <Alert variant="success">Your subscription is active. You can now use QCAR.</Alert>
        )}
      </Card>
    </div>
  );
};

export default BillingComplete;